import { Info, Plus } from 'lucide-react';
import { Button, EmptyState } from '@/shared/components';
import type { ArrivalStepResponse } from '@/shared/api';
import { ArrivalImagePanel } from './ArrivalImagePanel';
import { ArrivalStepCard } from './ArrivalStepCard';
import type { GuideCopy } from './guideCopy';

const APARTMENT_HINT =
  'Geerbtes kommt vom Objekt und lässt sich hier überschreiben, ohne die anderen Apartments zu ändern; Eigenes steht dahinter.';

interface Props {
  scope: 'object' | 'apartment';
  steps: ArrivalStepResponse[];
  copy: GuideCopy;
  canEdit: boolean;
  busy: boolean;
  openImages: string | null;
  onAdd: () => void;
  onEdit: (step: ArrivalStepResponse) => void;
  onMove: (step: ArrivalStepResponse, direction: 'Up' | 'Down') => void;
  onDelete: (step: ArrivalStepResponse) => void;
  onResetOverride: (step: ArrivalStepResponse) => void;
  onToggleImages: (stepId: string) => void;
}

/** Die Karte mit allen Schritten in der Reihenfolge, in der der Gast sie geht. */
export function ArrivalStepList(props: Props) {
  const { scope, steps, copy } = props;
  // Am Apartment lassen sich nur die eigenen Schritte verschieben — die geerbten gehören dem Objekt.
  const movable = steps.filter((step) => scope === 'object' || step.origin === 'Apartment');
  return (
    <section
      aria-label={copy.name}
      className="border-line/50 bg-card shadow-card rounded-lg border"
    >
      <div className="border-line/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <span className="text-ink-soft text-[11px] font-semibold uppercase tracking-[0.08em]">
          {copy.count(steps.length)}
        </span>
        {props.canEdit && (
          <Button
            variant="brand"
            className="min-h-11"
            leftIcon={<Plus size={14} strokeWidth={2} />}
            onClick={props.onAdd}
          >
            {copy.add}
          </Button>
        )}
      </div>
      {steps.length === 0 ? (
        <EmptyState icon="Pin" title={copy.emptyTitle} description={copy.emptyText} />
      ) : (
        <ol className="divide-line/60 divide-y px-5 py-4">
          {steps.map((step, index) => (
            <ArrivalStepCard
              key={step.id}
              step={step}
              number={index + 1}
              scope={scope}
              first={movable[0]?.id === step.id}
              last={movable[movable.length - 1]?.id === step.id}
              busy={props.busy}
              canEdit={props.canEdit}
              onEdit={() => props.onEdit(step)}
              onMove={(direction) => props.onMove(step, direction)}
              onDelete={() => props.onDelete(step)}
              onResetOverride={() => props.onResetOverride(step)}
              imagesOpen={props.openImages === step.id}
              onToggleImages={() => props.onToggleImages(step.id)}
              footer={
                props.openImages === step.id ? (
                  <ArrivalImagePanel stepId={step.id} canEdit={props.canEdit} />
                ) : null
              }
            />
          ))}
        </ol>
      )}
      <p className="border-line/60 text-ink-soft flex gap-2 border-t px-5 py-3 text-xs">
        <Info size={14} className="mt-px shrink-0" aria-hidden />
        {scope === 'object' ? copy.objectHint : APARTMENT_HINT}
      </p>
    </section>
  );
}
